/**
 * AssemblyEditorPage — Section-based NLE editor for assemblies.
 *
 * Supports scripted (scenes/takes) and unscripted (string-outs/selects)
 * workflows. Each section displays a horizontal clip strip with star
 * toggles for selecting best takes / selects.
 */

import { useState, useEffect, useCallback, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  ArrowLeft, Star, Plus, X, Trash2, Edit2, Check,
  ChevronUp, ChevronDown,
  Film, FolderOpen, GripVertical,
  Lock, Unlock, Info, RefreshCw, Loader2, EyeOff,
} from 'lucide-react'
import toast from 'react-hot-toast'
import apiClient from '../services/api'
import { isGenerationInProgress } from '../utils/generationStatus'
import ExportPanel from '../components/ExportPanel'
import {
  DndContext,
  DragOverlay,
  closestCenter,
  useDroppable,
  type DragStartEvent,
  type DragEndEvent,
  type DragOverEvent,
} from '@dnd-kit/core'
import {
  SortableContext,
  useSortable,
  horizontalListSortingStrategy,
  arrayMove,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { useDndSensors } from '../hooks/useDndSensors'
import { apiErrorDetail } from '../utils/dataValidator'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface SelectionReason {
  theme: string
  reason: string
  score: number
  score_breakdown: { audio: number; motion: number; speaker: number; total: number }
  source_asset_id?: number
  source_scene_index?: number
}

interface SectionMoment {
  id: number
  moment_id: number
  scene_index: number
  title: string | null
  thumbnail_url: string | null
  start_time: number | null
  end_time: number | null
  duration: number | null
  asset_filename: string | null
  sequence_order: number
  is_selected: boolean
  is_locked: boolean
  is_excluded: boolean
  ai_score: number | null
  selection_reason: SelectionReason | null
}

interface Section {
  id: number
  name: string
  description: string | null
  section_type: string
  section_order: number
  is_locked: boolean
  cluster_label: string | null
  moments: SectionMoment[]
  moments_count: number
  selected_count: number
}

interface AssemblyDetail {
  id: number
  project_id: string
  name: string
  description: string | null
  status: string
  generation_status: string
  sections: Section[]
  sections_count: number
  total_moments: number
  created_time: string | null
  updated_time: string | null
}

interface OutputClip {
  moment_id: number
  assembly_moment_id: number
  section_name: string
  section_order: number
  sequence_order: number
  title: string | null
  thumbnail_url: string | null
  start_time: number | null
  end_time: number | null
  duration: number | null
  asset_filename: string | null
}

interface OutputData {
  assembly_id: number
  name: string
  total_duration: number
  clip_count: number
  clips: OutputClip[]
}

interface MomentBrief {
  id: number
  scene_index: number
  title: string | null
  visual_description: string | null
  thumbnail_url: string | null
  start_time: number | null
  end_time: number | null
  asset_filename: string | null
}

interface BucketBrief { id: number; name: string; moments_count: number }
interface ClusterBrief { id: number; name: string; moments_count: number }

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatTime(seconds: number | null | undefined): string {
  if (seconds === null || seconds === undefined) return '0:00'
  const m = Math.floor(seconds / 60)
  const s = Math.floor(seconds % 60)
  return `${m}:${s.toString().padStart(2, '0')}`
}

// ---------------------------------------------------------------------------
// Sortable Clip Card
// ---------------------------------------------------------------------------

function ScoreBadge({ score }: { score: number | null }) {
  if (score === null || score === undefined) return null
  const pct = Math.round(score * 100)
  const color = pct >= 70 ? 'text-emerald-400 bg-emerald-400/15' : pct >= 40 ? 'text-amber-400 bg-amber-400/15' : 'text-[var(--rq-text-soft)] bg-white/5'
  return (
    <span className={`text-[9px] font-semibold px-1.5 py-0.5 rounded ${color}`}>
      {pct}
    </span>
  )
}

function LineageTooltip({ reason, onClose }: { reason: SelectionReason; onClose: () => void }) {
  const bd = reason.score_breakdown
  return (
    <div className="absolute z-50 top-full mt-1 left-0 w-56 bg-[var(--rq-surface)] border border-white/10 rounded-lg shadow-xl p-3 text-xs" onClick={e => e.stopPropagation()}>
      <div className="flex items-center justify-between mb-2">
        <span className="font-semibold text-[var(--rq-text)]">Why this clip</span>
        <button onClick={onClose} className="text-[var(--rq-text-soft)] hover:text-[var(--rq-text)]"><X className="w-3 h-3" /></button>
      </div>
      <p className="text-[var(--rq-text-soft)] mb-2">{reason.reason}</p>
      <div className="space-y-1.5">
        <div className="flex items-center gap-2">
          <span className="w-12 text-[var(--rq-text-soft)]">Audio</span>
          <div className="flex-1 h-1.5 bg-white/10 rounded-full"><div className="h-full bg-[var(--rq-blue)] rounded-full" style={{ width: `${Math.round((bd.audio || 0) * 100)}%` }} /></div>
        </div>
        <div className="flex items-center gap-2">
          <span className="w-12 text-[var(--rq-text-soft)]">Motion</span>
          <div className="flex-1 h-1.5 bg-white/10 rounded-full"><div className="h-full bg-emerald-400 rounded-full" style={{ width: `${Math.round((bd.motion || 0) * 100)}%` }} /></div>
        </div>
        <div className="flex items-center gap-2">
          <span className="w-12 text-[var(--rq-text-soft)]">Speaker</span>
          <div className="flex-1 h-1.5 bg-white/10 rounded-full"><div className="h-full bg-[var(--rq-amber)] rounded-full" style={{ width: `${Math.min(100, Math.round((bd.speaker || 0) * 100))}%` }} /></div>
        </div>
      </div>
      {reason.theme && (
        <p className="mt-2 text-[10px] text-[var(--rq-text-soft)]">Theme: {reason.theme}</p>
      )}
    </div>
  )
}

function SortableClip({
  moment,
  sectionId,
  draftReviewOnly,
  labels,
  onToggleSelect,
  onToggleLock,
  onToggleExclude,
  onRemove,
}: {
  moment: SectionMoment
  sectionId: number
  index: number
  total: number
  draftReviewOnly: boolean
  labels: { sectionLabel: string; addSection: string; sectionType: string; selectLabel: string; clipsLabel: string }
  onToggleSelect: (sectionId: number, momentId: number) => void
  onToggleLock: (sectionId: number, momentId: number) => void
  onToggleExclude: (sectionId: number, momentId: number) => void
  onRemove: (sectionId: number, momentId: number) => void
}) {
  const [showLineage, setShowLineage] = useState(false)

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: `clip-${sectionId}-${moment.moment_id}`,
    data: { type: 'assembly-clip' as const, sectionId, momentId: moment.moment_id },
    disabled: moment.is_locked,
  })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.3 : moment.is_excluded ? 0.4 : 1,
  }

  return (
    <div ref={setNodeRef} style={style} className={`flex-shrink-0 w-40 group relative ${moment.is_locked ? 'ring-1 ring-amber-400/30 rounded-lg' : ''}`}>
      <div className="relative aspect-video bg-black/40 rounded-lg overflow-hidden">
        {moment.thumbnail_url ? (
          <img
            src={moment.thumbnail_url}
            alt={moment.title || 'Clip'}
            className="w-full h-full object-cover"
            loading="lazy"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Film className="w-6 h-6 text-[var(--rq-text-soft)]" />
          </div>
        )}

        {/* Drag handle — hidden when locked */}
        {!moment.is_locked && (
          <div
            {...attributes}
            {...listeners}
            className="absolute top-1 left-1/2 -translate-x-1/2 p-0.5 rounded bg-black/50 text-white/60 hover:!text-white cursor-grab active:cursor-grabbing transition-all"
            title="Drag to reorder"
          >
            <GripVertical className="w-3.5 h-3.5 rotate-90" />
          </div>
        )}

        {/* Lock indicator */}
        {moment.is_locked && (
          <div className="absolute top-1 left-1 p-0.5 rounded bg-amber-500/80">
            <Lock className="w-3 h-3 text-white" />
          </div>
        )}

        {/* Top-right: select star + lock toggle */}
        <div className="absolute top-1 right-1 flex items-center gap-0.5">
          {!draftReviewOnly && (
            <button
              onClick={() => onToggleLock(sectionId, moment.moment_id)}
              className="p-1 rounded-full bg-black/30 text-white/60 hover:!text-amber-400 transition-all"
              title={moment.is_locked ? 'Unlock clip' : 'Lock clip'}
            >
              {moment.is_locked ? <Lock className="w-3 h-3" /> : <Unlock className="w-3 h-3" />}
            </button>
          )}
          {!draftReviewOnly && (
            <button
              onClick={() => onToggleSelect(sectionId, moment.moment_id)}
              className={`p-1 rounded-full transition-all ${
                moment.is_selected
                  ? 'text-yellow-400 bg-black/50'
                  : 'text-white/40 bg-black/30 hover:text-white/70'
              }`}
              title={moment.is_selected ? 'Deselect' : labels.selectLabel}
            >
              <Star className={`w-3.5 h-3.5 ${moment.is_selected ? 'fill-current' : ''}`} />
            </button>
          )}
        </div>

        {/* Bottom row: duration + score + actions */}
        <div className="absolute bottom-1 left-1 right-1 flex items-center justify-between">
          <div className="flex items-center gap-1">
            {moment.duration !== null && (
              <span className="text-[10px] bg-black/70 text-white px-1.5 py-0.5 rounded">
                {formatTime(moment.duration)}
              </span>
            )}
            <ScoreBadge score={moment.ai_score} />
          </div>

          <div className="flex items-center gap-0.5">
            {moment.selection_reason && (
              <button
                onClick={() => setShowLineage(!showLineage)}
                className="p-1 rounded-full bg-black/50 text-white/60 hover:!text-[var(--rq-blue)] transition-all"
                title="Why this clip"
              >
                <Info className="w-3.5 h-3.5" />
              </button>
            )}
            {!draftReviewOnly && !moment.is_locked && (
              <button
                onClick={() => onToggleExclude(sectionId, moment.moment_id)}
                className={`p-1 rounded-full bg-black/50 transition-all ${
                  moment.is_excluded
                    ? 'text-orange-400'
                    : 'text-white/60 hover:!text-orange-400'
                }`}
                title={moment.is_excluded ? 'Include clip' : 'Exclude from output'}
              >
                <EyeOff className="w-3.5 h-3.5" />
              </button>
            )}
            {!draftReviewOnly && !moment.is_locked && (
              <button
                onClick={() => onRemove(sectionId, moment.moment_id)}
                className="p-1 rounded-full bg-black/50 text-white/60 hover:!text-red-400 transition-all"
                title="Remove"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Lineage tooltip */}
      {showLineage && moment.selection_reason && (
        <LineageTooltip reason={moment.selection_reason} onClose={() => setShowLineage(false)} />
      )}

      <p className="text-xs text-[var(--rq-text-soft)] mt-1.5 truncate">
        {moment.title || moment.asset_filename || 'Untitled'}
      </p>
      <span className={`text-[9px] px-1.5 py-0.5 rounded ${
        moment.selection_reason
          ? 'text-sky-300 bg-sky-400/15'
          : 'text-emerald-300 bg-emerald-400/15'
      }`}>
        {moment.selection_reason ? 'AI Selected' : 'Editor Added'}
      </span>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Clip Drag Overlay (rendered in portal during drag)
// ---------------------------------------------------------------------------

function ClipOverlay({ moment }: { moment: SectionMoment }) {
  return (
    <div className="w-40 opacity-90 shadow-2xl rotate-2">
      <div className="relative aspect-video bg-black/40 rounded-lg overflow-hidden ring-2 ring-[var(--rq-blue)]">
        {moment.thumbnail_url ? (
          <img src={moment.thumbnail_url} alt="" className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Film className="w-6 h-6 text-[var(--rq-text-soft)]" />
          </div>
        )}
      </div>
      <p className="text-xs text-[var(--rq-text-soft)] mt-1.5 truncate">
        {moment.title || moment.asset_filename || 'Untitled'}
      </p>
    </div>
  )
}

function SectionDropZone({ sectionId, labels, draftReviewOnly, onAddClips }: {
  sectionId: number
  labels: { clipsLabel: string }
  draftReviewOnly: boolean
  onAddClips: () => void
}) {
  const { setNodeRef, isOver } = useDroppable({
    id: `section-drop-zone-${sectionId}`,
    data: { type: 'section-drop-zone', sectionId },
  })
  return (
    <div
      ref={setNodeRef}
      className={`text-center py-6 text-sm text-[var(--rq-text-soft)] border-2 border-dashed rounded-lg transition-colors ${
        isOver ? 'border-[var(--btn)] bg-[var(--btn)]/5 text-[var(--rq-text)]' : 'border-white/10'
      }`}
    >
      {isOver ? (
        `Drop ${labels.clipsLabel.toLowerCase()} here`
      ) : (
        <>
          No {labels.clipsLabel.toLowerCase()} yet.{' '}
          {!draftReviewOnly && (
            <button onClick={onAddClips} className="text-[var(--brand-light)] hover:underline">
              Add {labels.clipsLabel.toLowerCase()}
            </button>
          )}
        </>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function AssemblyEditorPage() {
  const { assemblyId } = useParams<{ assemblyId: string }>()
  const navigate = useNavigate()

  const [assembly, setAssembly] = useState<AssemblyDetail | null>(null)
  const [loading, setLoading] = useState(true)  // initial load only
  const hasLoadedOnce = useRef(false)
  const [error, setError] = useState<string | null>(null)

  // Output panel
  // Always shown. There is no control that closes it; a setter nothing
  // calls made the panel look toggleable when it never was.
  const [showOutput] = useState(true)
  const [output, setOutput] = useState<OutputData | null>(null)
  const [outputLoading, setOutputLoading] = useState(false)

  // Inline name edit
  const [editingName, setEditingName] = useState(false)
  const [nameValue, setNameValue] = useState('')

  // New section
  const [showNewSection, setShowNewSection] = useState(false)
  const [newSectionName, setNewSectionName] = useState('')

  // Section name editing
  const [editingSectionId, setEditingSectionId] = useState<number | null>(null)
  const [editSectionName, setEditSectionName] = useState('')

  // Add clips modal
  const [addToSectionId, setAddToSectionId] = useState<number | null>(null)
  const [addSourceTab, setAddSourceTab] = useState<'clips' | 'buckets' | 'clusters'>('clips')
  const [addSelectedIds, setAddSelectedIds] = useState<number[]>([])
  const [addingClips, setAddingClips] = useState(false)
  const [availableMoments, setAvailableMoments] = useState<MomentBrief[]>([])
  const [availableBuckets, setAvailableBuckets] = useState<BucketBrief[]>([])
  const [availableClusters, setAvailableClusters] = useState<ClusterBrief[]>([])
  const [sourcesLoaded, setSourcesLoaded] = useState(false)

  const labels = { sectionLabel: 'String-out', addSection: 'Add String-out', sectionType: 'string_out', selectLabel: 'Select', clipsLabel: 'Clips' }
  const draftReviewOnly = assembly?.status === 'draft'

  // --- DnD state ---
  const sensors = useDndSensors()
  const [activeClipId, setActiveClipId] = useState<string | null>(null)
  const sectionsSnapshot = useRef<Section[] | null>(null)

  // Find the moment being dragged (for overlay)
  const activeMoment = (() => {
    if (!activeClipId || !assembly) return null
    for (const s of assembly.sections) {
      for (const m of s.moments) {
        if (`clip-${s.id}-${m.moment_id}` === activeClipId) return m
      }
    }
    return null
  })()

  const handleDragStart = (event: DragStartEvent) => {
    const data = event.active.data.current
    if (data?.type === 'assembly-clip') {
      setActiveClipId(event.active.id as string)
      if (assembly) {
        sectionsSnapshot.current = assembly.sections.map(s => ({
          ...s,
          moments: [...s.moments],
        }))
      }
    }
  }

  const handleDragOver = (event: DragOverEvent) => {
    const { active, over } = event
    if (!over || !assembly) return
    const activeData = active.data.current
    const overData = over.data.current
    if (activeData?.type !== 'assembly-clip') return

    const activeSectionId = activeData.sectionId as number
    const activeMomentId = activeData.momentId as number

    // Determine target section from the over item
    let overSectionId: number | undefined
    if (overData?.type === 'assembly-clip') {
      overSectionId = overData.sectionId as number
    } else if (overData?.type === 'section-drop-zone') {
      overSectionId = overData.sectionId as number
    }

    if (!overSectionId || activeSectionId === overSectionId) return

    // Cross-section: optimistically move moment to target section
    setAssembly(prev => {
      if (!prev) return prev
      const sourceSection = prev.sections.find(s => s.id === activeSectionId)
      const targetSection = prev.sections.find(s => s.id === overSectionId)
      if (!sourceSection || !targetSection) return prev

      const movingMoment = sourceSection.moments.find(m => m.moment_id === activeMomentId)
      if (!movingMoment) return prev

      // Already in target? skip
      if (targetSection.moments.some(m => m.moment_id === activeMomentId)) return prev

      const newSource = sourceSection.moments.filter(m => m.moment_id !== activeMomentId)
      const newTarget = [...targetSection.moments, { ...movingMoment }]

      return {
        ...prev,
        sections: prev.sections.map(s => {
          if (s.id === activeSectionId) return { ...s, moments: newSource, moments_count: newSource.length }
          if (s.id === overSectionId) return { ...s, moments: newTarget, moments_count: newTarget.length }
          return s
        }),
      }
    })

    // Update the active item's data so subsequent events know the new section
    active.data.current = { ...activeData, sectionId: overSectionId }
  }

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event
    setActiveClipId(null)

    if (!over || !assembly) {
      // Cancelled — revert
      if (sectionsSnapshot.current) {
        setAssembly(prev => prev ? { ...prev, sections: sectionsSnapshot.current! } : prev)
      }
      sectionsSnapshot.current = null
      return
    }

    const activeData = active.data.current
    if (activeData?.type !== 'assembly-clip') {
      sectionsSnapshot.current = null
      return
    }

    const currentSectionId = activeData.sectionId as number
    const momentId = activeData.momentId as number
    const originalSectionId = sectionsSnapshot.current
      ?.find(s => s.moments.some(m => m.moment_id === momentId))?.id

    // Find the moment's index in the current section after optimistic moves
    const currentSection = assembly.sections.find(s => s.id === currentSectionId)
    if (!currentSection) {
      sectionsSnapshot.current = null
      return
    }

    const orderedMoments = [...currentSection.moments].sort((a, b) => a.sequence_order - b.sequence_order)
    const oldIndex = orderedMoments.findIndex(m => `clip-${currentSectionId}-${m.moment_id}` === active.id)
    const newIndex = orderedMoments.findIndex(m => `clip-${currentSectionId}-${m.moment_id}` === over.id)

    if (originalSectionId && originalSectionId !== currentSectionId) {
      // Cross-section move — call backend
      const insertIndex = newIndex >= 0 ? newIndex : orderedMoments.length - 1
      try {
        await apiClient.patch(
          `/assemblies/${assembly.id}/sections/${currentSectionId}/moments/move`,
          { moment_id: momentId, from_section_id: originalSectionId, insert_index: insertIndex }
        )
        fetchAssembly()
        if (showOutput) fetchOutput()
      } catch {
        toast.error('Failed to move clip')
        if (sectionsSnapshot.current) {
          setAssembly(prev => prev ? { ...prev, sections: sectionsSnapshot.current! } : prev)
        }
      }
    } else if (oldIndex !== -1 && newIndex !== -1 && oldIndex !== newIndex) {
      // Same-section reorder
      const newOrder = arrayMove(orderedMoments.map(m => m.moment_id), oldIndex, newIndex)
      // Optimistic update
      const newMoments = newOrder.map((mid, i) => {
        const m = orderedMoments.find(om => om.moment_id === mid)!
        return { ...m, sequence_order: i }
      })
      setAssembly(prev => {
        if (!prev) return prev
        return {
          ...prev,
          sections: prev.sections.map(s =>
            s.id === currentSectionId ? { ...s, moments: newMoments } : s
          ),
        }
      })

      try {
        await apiClient.patch(
          `/assemblies/${assembly.id}/sections/${currentSectionId}/reorder`,
          { moment_ids: newOrder }
        )
        if (showOutput) fetchOutput()
      } catch {
        toast.error('Failed to reorder clips')
        fetchAssembly()
      }
    }

    sectionsSnapshot.current = null
  }

  const handleDragCancel = () => {
    setActiveClipId(null)
    if (sectionsSnapshot.current) {
      setAssembly(prev => prev ? { ...prev, sections: sectionsSnapshot.current! } : prev)
    }
    sectionsSnapshot.current = null
  }

  // --- Fetch assembly ---
  const fetchAssembly = useCallback(async () => {
    try {
      if (!hasLoadedOnce.current) setLoading(true)
      const res = await apiClient.get(`/assemblies/${assemblyId}`)
      setAssembly(res.data)
      setError(null)
      hasLoadedOnce.current = true
    } catch (err) {
      setError(apiErrorDetail(err, 'Failed to load assembly'))
    } finally {
      setLoading(false)
    }
  }, [assemblyId])

  useEffect(() => { fetchAssembly() }, [fetchAssembly])

  // --- Poll while generation is in progress ---
  const generationStatus = assembly?.generation_status
  useEffect(() => {
    if (!isGenerationInProgress(generationStatus)) return
    const interval = setInterval(() => { fetchAssembly() }, 3000)
    return () => clearInterval(interval)
  }, [generationStatus, fetchAssembly])

  const isGenerating = isGenerationInProgress(assembly?.generation_status)

  // --- Fetch output ---
  const fetchOutput = useCallback(async () => {
    try {
      setOutputLoading(true)
      const res = await apiClient.get(`/assemblies/${assemblyId}/output`)
      setOutput(res.data)
    } catch {
      toast.error('Failed to load output')
    } finally {
      setOutputLoading(false)
    }
  }, [assemblyId])

  useEffect(() => {
    if (showOutput) fetchOutput()
  }, [showOutput, fetchOutput])

  // Refresh output when generation completes
  const prevGenStatus = useRef<string | undefined>()
  useEffect(() => {
    const status = assembly?.generation_status
    if (prevGenStatus.current && prevGenStatus.current !== 'ready' && status === 'ready') {
      if (showOutput) fetchOutput()
    }
    prevGenStatus.current = status
  }, [assembly?.generation_status, showOutput, fetchOutput])

  // --- Fetch sources for add-clips modal ---
  const fetchSources = useCallback(async () => {
    if (!assembly || sourcesLoaded) return
    try {
      // Scope to the assembly's project to maintain project boundary integrity
      const projectId = assembly.project_id

      const [momRes, buckRes, clustRes] = await Promise.all([
        apiClient.get('/moments/', { params: { project_id: projectId, limit: 200 } }),
        apiClient.get('/buckets/', { params: { project_id: projectId } }),
        apiClient.get('/clusters/', { params: { project_id: projectId } }),
      ])
      setAvailableMoments(momRes.data.moments)
      setAvailableBuckets(buckRes.data.buckets.map((b: { id: number; name: string; moments_count: number }) => ({ id: b.id, name: b.name, moments_count: b.moments_count })))
      setAvailableClusters(clustRes.data.clusters.map((c: { id: number; name: string; moments_count: number }) => ({ id: c.id, name: c.name, moments_count: c.moments_count })))
      setSourcesLoaded(true)
    } catch {
      toast.error('Failed to load clip sources')
    }
  }, [assembly, sourcesLoaded])



  // --- Name editing ---
  const startEditName = () => {
    if (!assembly) return
    setNameValue(assembly.name)
    setEditingName(true)
  }

  const saveName = async () => {
    if (!nameValue.trim() || !assembly) return
    try {
      await apiClient.patch(`/assemblies/${assembly.id}`, { name: nameValue.trim() })
      setEditingName(false)
      fetchAssembly()
    } catch (err) {
      toast.error(apiErrorDetail(err, 'Failed to rename'))
    }
  }

  // --- Status transitions ---
  const transitionStatus = async (newStatus: string) => {
    if (!assembly) return
    try {
      await apiClient.patch(`/assemblies/${assembly.id}`, { status: newStatus })
      fetchAssembly()
      toast.success(`Status changed to ${newStatus}`)
    } catch (err) {
      toast.error(apiErrorDetail(err, 'Failed to update status'))
    }
  }

  // --- Section CRUD ---
  const createSection = async () => {
    if (!newSectionName.trim() || !assembly) return
    try {
      await apiClient.post(`/assemblies/${assembly.id}/sections`, {
        name: newSectionName.trim(),
      })
      setNewSectionName('')
      setShowNewSection(false)
      fetchAssembly()
    } catch (err) {
      toast.error(apiErrorDetail(err, 'Failed to create section'))
    }
  }

  const deleteSection = async (sectionId: number) => {
    if (!assembly) return
    try {
      await apiClient.delete(`/assemblies/${assembly.id}/sections/${sectionId}`)
      fetchAssembly()
      if (showOutput) fetchOutput()
    } catch (err) {
      toast.error(apiErrorDetail(err, 'Failed to delete section'))
    }
  }

  const saveSectionName = async (sectionId: number) => {
    if (!editSectionName.trim() || !assembly) return
    try {
      await apiClient.patch(`/assemblies/${assembly.id}/sections/${sectionId}`, {
        name: editSectionName.trim(),
      })
      setEditingSectionId(null)
      fetchAssembly()
    } catch (err) {
      toast.error(apiErrorDetail(err, 'Failed to rename section'))
    }
  }

  const reorderSections = async (sectionId: number, direction: 'up' | 'down') => {
    if (!assembly) return
    const sections = [...assembly.sections].sort((a, b) => a.section_order - b.section_order)
    const idx = sections.findIndex(s => s.id === sectionId)
    if (idx < 0) return
    if (direction === 'up' && idx === 0) return
    if (direction === 'down' && idx === sections.length - 1) return

    const newOrder = sections.map(s => s.id)
    const swapIdx = direction === 'up' ? idx - 1 : idx + 1
    ;[newOrder[idx], newOrder[swapIdx]] = [newOrder[swapIdx], newOrder[idx]]

    // Optimistic update
    const newSections = newOrder.map((sid, i) => {
      const s = sections.find(sec => sec.id === sid)!
      return { ...s, section_order: i }
    })
    setAssembly(prev => prev ? { ...prev, sections: newSections } : prev)

    try {
      await apiClient.patch(`/assemblies/${assembly.id}/sections/reorder`, {
        section_ids: newOrder,
      })
      if (showOutput) fetchOutput()
    } catch {
      toast.error('Failed to reorder sections')
      fetchAssembly()
    }
  }

  // --- Moment interactions ---
  const toggleSelect = async (sectionId: number, momentId: number) => {
    if (!assembly) return

    // Optimistic update
    setAssembly(prev => {
      if (!prev) return prev
      return {
        ...prev,
        sections: prev.sections.map(s => {
          if (s.id !== sectionId) return s
          const newMoments = s.moments.map(m =>
            m.moment_id === momentId ? { ...m, is_selected: !m.is_selected } : m
          )
          return {
            ...s,
            moments: newMoments,
            selected_count: newMoments.filter(m => m.is_selected).length,
          }
        }),
      }
    })

    try {
      await apiClient.patch(
        `/assemblies/${assembly.id}/sections/${sectionId}/moments/${momentId}/select`
      )
      if (showOutput) fetchOutput()
    } catch {
      toast.error('Failed to toggle selection')
      fetchAssembly()
    }
  }

  const toggleLock = async (sectionId: number, momentId: number) => {
    if (!assembly) return

    // Optimistic update
    setAssembly(prev => {
      if (!prev) return prev
      return {
        ...prev,
        sections: prev.sections.map(s => {
          if (s.id !== sectionId) return s
          return {
            ...s,
            moments: s.moments.map(m =>
              m.moment_id === momentId ? { ...m, is_locked: !m.is_locked } : m
            ),
          }
        }),
      }
    })

    try {
      await apiClient.patch(
        `/assemblies/${assembly.id}/sections/${sectionId}/moments/${momentId}/lock`
      )
    } catch {
      toast.error('Failed to toggle lock')
      fetchAssembly()
    }
  }

  const toggleExclude = async (sectionId: number, momentId: number) => {
    if (!assembly) return

    setAssembly(prev => {
      if (!prev) return prev
      return {
        ...prev,
        sections: prev.sections.map(s => {
          if (s.id !== sectionId) return s
          return {
            ...s,
            moments: s.moments.map(m =>
              m.moment_id === momentId ? { ...m, is_excluded: !m.is_excluded } : m
            ),
          }
        }),
      }
    })

    try {
      await apiClient.patch(
        `/assemblies/${assembly.id}/sections/${sectionId}/moments/${momentId}/exclude`
      )
      if (showOutput) fetchOutput()
    } catch {
      toast.error('Failed to toggle exclusion')
      fetchAssembly()
    }
  }

  const [regeneratingSection, setRegeneratingSection] = useState<number | null>(null)

  const regenerateSection = async (sectionId: number) => {
    if (!assembly || regeneratingSection !== null) return

    setRegeneratingSection(sectionId)
    try {
      await apiClient.post(
        `/assemblies/${assembly.id}/sections/${sectionId}/regenerate`
      )
      toast.success('Section regenerated')
      fetchAssembly()
      if (showOutput) fetchOutput()
    } catch {
      toast.error('Failed to regenerate section')
    } finally {
      setRegeneratingSection(null)
    }
  }

  const toggleSectionLock = async (sectionId: number) => {
    if (!assembly) return

    setAssembly(prev => {
      if (!prev) return prev
      return {
        ...prev,
        sections: prev.sections.map(s =>
          s.id === sectionId ? { ...s, is_locked: !s.is_locked } : s
        ),
      }
    })

    try {
      await apiClient.patch(
        `/assemblies/${assembly.id}/sections/${sectionId}/lock`
      )
    } catch {
      toast.error('Failed to toggle section lock')
      fetchAssembly()
    }
  }

  const removeMoment = async (sectionId: number, momentId: number) => {
    if (!assembly) return
    try {
      await apiClient.delete(
        `/assemblies/${assembly.id}/sections/${sectionId}/moments/${momentId}`
      )
      fetchAssembly()
      if (showOutput) fetchOutput()
    } catch (err) {
      toast.error(apiErrorDetail(err, 'Failed to remove clip'))
    }
  }

  // --- Add clips modal ---
  const openAddClips = (sectionId: number) => {
    setAddToSectionId(sectionId)
    setAddSelectedIds([])
    setAddSourceTab('clips')
    setAddingClips(false)
    fetchSources()
  }

  const closeAddClips = () => {
    setAddToSectionId(null)
    setAddSelectedIds([])
  }

  const toggleAddMoment = (id: number) => {
    setAddSelectedIds(prev =>
      prev.includes(id) ? prev.filter(mid => mid !== id) : [...prev, id]
    )
  }

  const submitAddClips = async () => {
    if (!assembly || addToSectionId === null || addSelectedIds.length === 0) return
    try {
      setAddingClips(true)
      await apiClient.post(
        `/assemblies/${assembly.id}/sections/${addToSectionId}/moments`,
        { moment_ids: addSelectedIds }
      )
      toast.success(`Added ${addSelectedIds.length} clip${addSelectedIds.length !== 1 ? 's' : ''}`)
      closeAddClips()
      fetchAssembly()
    } catch (err) {
      toast.error(apiErrorDetail(err, 'Failed to add clips'))
    } finally {
      setAddingClips(false)
    }
  }

  const addBucketToSection = async (bucketId: number) => {
    if (!assembly || addToSectionId === null) return
    try {
      await apiClient.post(
        `/assemblies/${assembly.id}/sections/${addToSectionId}/moments`,
        { bucket_id: bucketId }
      )
      toast.success('Added bucket clips')
      closeAddClips()
      fetchAssembly()
    } catch (err) {
      toast.error(apiErrorDetail(err, 'Failed to add bucket clips'))
    }
  }

  const addClusterToSection = async (clusterId: number) => {
    if (!assembly || addToSectionId === null) return
    try {
      await apiClient.post(
        `/assemblies/${assembly.id}/sections/${addToSectionId}/moments`,
        { cluster_id: clusterId }
      )
      toast.success('Added cluster clips')
      closeAddClips()
      fetchAssembly()
    } catch (err) {
      toast.error(apiErrorDetail(err, 'Failed to add cluster clips'))
    }
  }

  // Existing moment IDs in the target section (for dedup display)
  const existingMomentIdsInSection = (() => {
    if (!assembly || addToSectionId === null) return new Set<number>()
    const section = assembly.sections.find(s => s.id === addToSectionId)
    if (!section) return new Set<number>()
    return new Set(section.moments.map(m => m.moment_id))
  })()

  const availableForAdd = availableMoments.filter(m => !existingMomentIdsInSection.has(m.id))

  // --- Loading / error states ---
  if (loading) {
    return <div className="p-8 text-center text-[var(--rq-text-soft)]">Loading assembly...</div>
  }

  if (error || !assembly) {
    return (
      <div className="p-8 text-center">
        <p className="text-red-400 mb-4">{error || 'Assembly not found'}</p>
        <button onClick={() => navigate('/assemblies')} className="text-[var(--brand-light)] hover:underline text-sm">
          Back to assemblies
        </button>
      </div>
    )
  }

  const sortedSections = [...assembly.sections].sort((a, b) => a.section_order - b.section_order)

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="border-b border-white/10 bg-black/20 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto w-full flex items-center gap-4 px-6 md:px-8 py-4">
          <button
            onClick={() => navigate('/assemblies')}
            className="text-[var(--rq-text-soft)] hover:text-[var(--rq-text)] transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>

        {!draftReviewOnly && editingName ? (
          <div className="flex items-center gap-2 flex-1">
            <input
              autoFocus
              value={nameValue}
              onChange={e => setNameValue(e.target.value)}
              className="rq-input-dark text-lg font-semibold flex-1 px-3 py-1.5"
              onKeyDown={e => { if (e.key === 'Enter') saveName(); if (e.key === 'Escape') setEditingName(false) }}
            />
            <button onClick={saveName} className="text-green-400 hover:text-green-300 p-1">
              <Check className="w-5 h-5" />
            </button>
            <button onClick={() => setEditingName(false)} className="text-[var(--rq-text-soft)] hover:text-[var(--rq-text)] p-1">
              <X className="w-5 h-5" />
            </button>
          </div>
        ) : (
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <h1 className="rq-title text-xl md:text-2xl truncate">{assembly.name}</h1>
            {draftReviewOnly ? (
              <span className="text-[10px] px-2 py-0.5 rounded-full border border-white/15 text-[var(--rq-text-soft)]">
                AI first draft
              </span>
            ) : (
              <button onClick={startEditName} className="group">
                <Edit2 className="w-4 h-4 text-[var(--rq-text-soft)] opacity-70 group-hover:opacity-100 transition-opacity flex-shrink-0" />
              </button>
            )}
          </div>
        )}

        <div className="flex items-center gap-2 flex-shrink-0">
          <span className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full border border-white/20 bg-white/5 text-[var(--rq-text-soft)]">
            <Film className="w-3 h-3" /> AI Draft
          </span>
          {assembly.status === 'draft' && (
            <button
              onClick={() => transitionStatus('review')}
              className="inline-flex items-center gap-1 text-xs px-3 py-1 rounded-full border border-amber-500/40 bg-amber-500/10 text-amber-200 hover:bg-amber-500/20 transition-colors"
            >
              Ready for Review
            </button>
          )}
          {assembly.status === 'review' && (
            <>
              <span className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full border border-amber-500/40 bg-amber-500/10 text-amber-200">
                In Review
              </span>
              <button
                onClick={() => transitionStatus('final')}
                className="inline-flex items-center gap-1 text-xs px-3 py-1 rounded-full border border-emerald-500/40 bg-emerald-500/10 text-emerald-200 hover:bg-emerald-500/20 transition-colors"
              >
                Mark Final
              </button>
              <button
                onClick={() => transitionStatus('draft')}
                className="text-xs text-[var(--rq-text-soft)] hover:text-[var(--rq-text)] transition-colors"
              >
                Back to Draft
              </button>
            </>
          )}
          {assembly.status === 'final' && (
            <span className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full border border-emerald-500/40 bg-emerald-500/10 text-emerald-200">
              <Check className="w-3 h-3" /> Final
            </span>
          )}
        </div>
      </div>
      </div>

      {/* AI Summary bar */}
      {(() => {
        const totalClips = assembly.sections.reduce((sum, s) => sum + s.moments.length, 0)
        const selectedClips = assembly.sections.reduce((sum, s) => sum + s.moments.filter(m => m.is_selected && !m.is_excluded).length, 0)
        const draftSecs = assembly.sections.reduce((sum, s) => sum + s.moments.filter(m => m.is_selected && !m.is_excluded).reduce((d, m) => d + (m.duration || 0), 0), 0)
        const mins = Math.floor(draftSecs / 60)
        const secs = Math.floor(draftSecs % 60)
        return (
          <div className="border-b border-white/10 bg-black/10 px-6 md:px-8 py-2 text-xs text-[var(--rq-text-soft)] flex items-center gap-4">
            <span>Sections: <span className="text-[var(--rq-text)]">{assembly.sections.length}</span></span>
            <span className="text-white/20">·</span>
            <span>Selected: <span className="text-[var(--rq-text)]">{selectedClips}</span> / {totalClips} clips</span>
            <span className="text-white/20">·</span>
            <span>Duration: <span className="text-[var(--rq-text)]">{mins}:{secs.toString().padStart(2, '0')}</span></span>
          </div>
        )
      })()}

      {/* Main content */}
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={handleDragStart}
        onDragOver={handleDragOver}
        onDragEnd={handleDragEnd}
        onDragCancel={handleDragCancel}
      >
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-7xl mx-auto w-full p-6 md:p-8 space-y-6">
        {/* Generation in progress banner */}
        {isGenerating && (
          <div className="flex items-center gap-3 px-4 py-3 rounded-lg bg-[var(--brand-soft)]/10 border border-[var(--brand-light)]/20">
            <Loader2 className="w-5 h-5 text-[var(--brand-light)] animate-spin flex-shrink-0" />
            <div>
              <p className="text-sm font-medium text-[var(--rq-text)]">Generating AI draft...</p>
              <p className="text-xs text-[var(--rq-text-soft)]">Scoring, clustering, and selecting clips. This page will update automatically.</p>
            </div>
          </div>
        )}

        {/* Sections */}
        {sortedSections.length === 0 && !showNewSection && !isGenerating && (
          <div className="text-center py-20">
            <FolderOpen className="w-16 h-16 text-[var(--rq-text-soft)] mx-auto mb-4" />
            <h2 className="text-lg font-semibold text-[var(--rq-text)] mb-2">No {labels.sectionLabel.toLowerCase()}s yet</h2>
            <p className="text-[var(--rq-text-soft)] max-w-md mx-auto mb-6">
              Add your first {labels.sectionLabel.toLowerCase()} to start shaping this AI Draft.
            </p>
            {!draftReviewOnly && (
              <button
                onClick={() => setShowNewSection(true)}
                className="rq-btn-primary px-4 py-2 text-sm"
              >
                <Plus className="w-4 h-4" />
                {labels.addSection}
              </button>
            )}
          </div>
        )}

        {sortedSections.map((section, sIdx) => {
          const orderedMoments = [...section.moments].sort((a, b) => a.sequence_order - b.sequence_order)
          const clipIds = orderedMoments.map(m => `clip-${section.id}-${m.moment_id}`)

          return (
            <div key={section.id} className="rq-card overflow-hidden">
              {/* Section header */}
              <div className="flex flex-wrap items-center gap-2 px-4 py-3 border-b border-white/10">
                {!draftReviewOnly && (
                  <div className="flex flex-col gap-0.5">
                    <button
                      onClick={() => reorderSections(section.id, 'up')}
                      disabled={sIdx === 0}
                      className="text-[var(--rq-text-soft)] hover:text-[var(--rq-text)] disabled:opacity-20 transition-colors"
                    >
                      <ChevronUp className="w-3.5 h-3.5" />
                    </button>
                    <button
                      onClick={() => reorderSections(section.id, 'down')}
                      disabled={sIdx === sortedSections.length - 1}
                      className="text-[var(--rq-text-soft)] hover:text-[var(--rq-text)] disabled:opacity-20 transition-colors"
                    >
                      <ChevronDown className="w-3.5 h-3.5" />
                    </button>
                  </div>
                )}

                {/* Section name */}
                <span className="text-xs text-[var(--rq-text-soft)] font-medium uppercase tracking-wider">
                  {labels.sectionLabel} {sIdx + 1}:
                </span>

                {!draftReviewOnly && editingSectionId === section.id ? (
                  <div className="flex items-center gap-2 flex-1">
                    <input
                      autoFocus
                      value={editSectionName}
                      onChange={e => setEditSectionName(e.target.value)}
                      className="rq-input-dark text-sm flex-1 px-2 py-1"
                      onKeyDown={e => { if (e.key === 'Enter') saveSectionName(section.id); if (e.key === 'Escape') setEditingSectionId(null) }}
                    />
                    <button onClick={() => saveSectionName(section.id)} className="text-green-400 hover:text-green-300 p-1">
                      <Check className="w-4 h-4" />
                    </button>
                    <button onClick={() => setEditingSectionId(null)} className="text-[var(--rq-text-soft)] hover:text-[var(--rq-text)] p-1">
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <p className="text-sm text-[var(--rq-text)] font-medium truncate flex-1 text-left">
                    {section.name}
                  </p>
                )}

                {/* Selected count badge */}
                <span className="text-xs text-[var(--rq-text-soft)] ml-auto">
                  Selected {section.selected_count}/{section.moments_count}
                </span>

                {/* Actions */}
                {!draftReviewOnly && (
                  <>
                    <button
                      onClick={() => toggleSectionLock(section.id)}
                      className={`p-1 transition-colors ${section.is_locked ? 'text-amber-400' : 'text-[var(--rq-text-soft)] hover:text-amber-400'}`}
                      title={section.is_locked ? 'Unlock section' : 'Lock section'}
                    >
                      {section.is_locked ? <Lock className="w-4 h-4" /> : <Unlock className="w-4 h-4" />}
                    </button>
                    <button
                      onClick={() => regenerateSection(section.id)}
                      disabled={section.is_locked || regeneratingSection !== null}
                      className="text-[var(--rq-text-soft)] hover:text-[var(--brand-light)] disabled:opacity-30 p-1 transition-colors"
                      title={section.is_locked ? 'Unlock to regenerate' : 'Regenerate section'}
                    >
                      {regeneratingSection === section.id ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <RefreshCw className="w-4 h-4" />
                      )}
                    </button>
                    <button
                      onClick={() => openAddClips(section.id)}
                      className="text-[var(--rq-text-soft)] hover:text-[var(--brand-light)] p-1 transition-colors"
                      title={`Add ${labels.clipsLabel.toLowerCase()}`}
                    >
                      <Plus className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => deleteSection(section.id)}
                      className="text-[var(--rq-text-soft)] hover:text-red-400 p-1 transition-colors"
                      title="Delete section"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </>
                )}
              </div>

              {/* Clip strip with sortable context */}
              <div className="p-3">
                {orderedMoments.length === 0 ? (
                  <SectionDropZone
                    sectionId={section.id}
                    labels={labels}
                    draftReviewOnly={draftReviewOnly}
                    onAddClips={() => openAddClips(section.id)}
                  />
                ) : (
                  <SortableContext items={clipIds} strategy={horizontalListSortingStrategy}>
                    <div className="flex gap-3 overflow-x-auto pb-1">
                      {orderedMoments.map((moment, mIdx) => (
                        <SortableClip
                          key={`clip-${section.id}-${moment.moment_id}`}
                          moment={moment}
                          sectionId={section.id}
                          index={mIdx}
                          total={orderedMoments.length}
                          draftReviewOnly={draftReviewOnly}
                          labels={labels}
                          onToggleSelect={toggleSelect}
                          onToggleLock={toggleLock}
                          onToggleExclude={toggleExclude}
                          onRemove={removeMoment}
                        />
                      ))}
                    </div>
                  </SortableContext>
                )}
              </div>
            </div>
          )
        })}

        {/* Add section button / inline form */}
        {!draftReviewOnly && (sortedSections.length > 0 || showNewSection) && (
          <div className="flex justify-center py-6">
            {showNewSection ? (
              <div className="flex items-center gap-2 w-full max-w-md">
                <input
                  autoFocus
                  value={newSectionName}
                  onChange={e => setNewSectionName(e.target.value)}
                  placeholder={`${labels.sectionLabel} name...`}
                  className="flex-1 rq-input-dark text-sm"
                  onKeyDown={e => { if (e.key === 'Enter') createSection(); if (e.key === 'Escape') setShowNewSection(false) }}
                />
                <button
                  onClick={createSection}
                  disabled={!newSectionName.trim()}
                  className="rq-btn-primary px-4 py-2 disabled:opacity-40 text-sm"
                >
                  Add
                </button>
                <button
                  onClick={() => { setShowNewSection(false); setNewSectionName('') }}
                  className="text-[var(--rq-text-soft)] hover:text-[var(--rq-text)] p-2 transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <button
                onClick={() => setShowNewSection(true)}
                  className="flex items-center gap-2 px-4 py-2 border border-dashed border-white/20 rounded-lg text-sm text-[var(--rq-text-soft)] hover:text-[var(--rq-text)] hover:border-white/40 transition-colors"
              >
                <Plus className="w-4 h-4" />
                {labels.addSection}
              </button>
            )}
          </div>
        )}
        </div>
      </div>

      <DragOverlay>
        {activeMoment ? <ClipOverlay moment={activeMoment} /> : null}
      </DragOverlay>
      </DndContext>

      {/* Output panel */}
      {showOutput && (
        <div className="border-t border-white/10 bg-black/25 backdrop-blur-sm">
          <div className="max-w-7xl mx-auto w-full px-6 md:px-8 py-4">
          <div className="mb-3">
            <h3 className="text-sm font-medium text-[var(--rq-text)] mb-3">Draft Export</h3>
            {assembly && (
              <ExportPanel
                assemblyId={assembly.id}
                assemblyName={assembly.name}
                clipCount={output?.clip_count ?? 0}
                totalDuration={output?.total_duration ?? 0}
              />
            )}
          </div>

          {outputLoading ? (
            <div className="text-center py-4 text-sm text-[var(--rq-text-soft)]">Loading output...</div>
          ) : !output || output.clips.length === 0 ? (
            <div className="text-center py-4 text-sm text-[var(--rq-text-soft)]">
              No clips in this draft export.
            </div>
          ) : (
            <div className="flex gap-3 overflow-x-auto pb-1">
              {output.clips.map((clip, i) => (
                <div key={clip.assembly_moment_id} className="flex-shrink-0 w-32">
                  <div className="relative aspect-video bg-black/35 rounded-lg overflow-hidden">
                    {clip.thumbnail_url ? (
                      <img src={clip.thumbnail_url} alt={clip.title || 'Clip'} className="w-full h-full object-cover" loading="lazy" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Film className="w-5 h-5 text-[var(--rq-text-soft)]" />
                      </div>
                    )}
                    <span className="absolute top-1 left-1 text-[10px] bg-[var(--btn)] text-white px-1.5 py-0.5 rounded-full font-medium">
                      {i + 1}
                    </span>
                    {clip.duration !== null && (
                      <span className="absolute bottom-1 right-1 text-[10px] bg-black/70 text-white px-1.5 py-0.5 rounded">
                        {formatTime(clip.duration)}
                      </span>
                    )}
                  </div>
                  <p className="text-[10px] text-[var(--rq-text-soft)] mt-1 truncate">{clip.section_name}</p>
                  <p className="text-xs text-[var(--rq-text-soft)] truncate">{clip.title || clip.asset_filename || 'Untitled'}</p>
                </div>
              ))}
            </div>
          )}
          </div>
        </div>
      )}

      {/* ---- Add Clips Modal ---- */}
      {!draftReviewOnly && addToSectionId !== null && (
        <div className="rq-modal-overlay">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={closeAddClips} />

          <div className="relative w-full max-w-lg mx-4 rq-modal-card max-h-[85vh] flex flex-col">
            <FolderOpen className="w-10 h-10 text-[var(--rq-text-soft)] mb-4" />
            <h2 className="text-xl font-bold text-[var(--rq-text)] mb-1">Add {labels.clipsLabel}</h2>
            <p className="text-sm text-[var(--rq-text-soft)] mb-5">
              Select clips to add to this {labels.sectionLabel.toLowerCase()}.
            </p>

            {/* Selected count banner */}
            {addSelectedIds.length > 0 && addSourceTab === 'clips' && (
              <div className="flex items-center gap-3 bg-emerald-900/30 border border-emerald-700/40 rounded-xl px-4 py-3 mb-5">
                <div className="w-6 h-6 rounded-full bg-emerald-500 flex items-center justify-center flex-shrink-0">
                  <Plus className="w-3.5 h-3.5 text-white" />
                </div>
                <span className="text-sm text-emerald-200">
                  {addSelectedIds.length} clip{addSelectedIds.length !== 1 ? 's' : ''} selected
                </span>
              </div>
            )}

            {/* Source tabs */}
            <div className="flex border-b border-white/10 mb-3">
              {(['clips', 'buckets', 'clusters'] as const).map(tab => (
                <button
                  key={tab}
                  onClick={() => setAddSourceTab(tab)}
                  className={`flex-1 py-2 text-sm font-medium transition-colors ${
                    addSourceTab === tab
                      ? 'text-white border-b-2 border-[var(--btn)]'
                      : 'text-[var(--rq-text-soft)] hover:text-[var(--rq-text)]'
                  }`}
                >
                  {tab.charAt(0).toUpperCase() + tab.slice(1)}
                </button>
              ))}
            </div>

            {/* Source content */}
            <div className="flex-1 min-h-0 overflow-y-auto border border-white/10 rounded-xl bg-black/25 mb-5">
              {addSourceTab === 'clips' && (
                availableForAdd.length === 0 ? (
                  <div className="text-center py-8 text-sm text-[var(--rq-text-soft)]">
                    {availableMoments.length === 0
                      ? 'No clips available — upload videos and analyze them first.'
                      : 'All clips are already in this section.'}
                  </div>
                ) : (
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 p-3">
                    {availableForAdd.map(m => {
                      const isSelected = addSelectedIds.includes(m.id)
                      return (
                        <button
                          key={m.id}
                          onClick={() => toggleAddMoment(m.id)}
                          className={`relative rounded-lg overflow-hidden border transition-all text-left ${
                            isSelected
                              ? 'border-[var(--btn)] ring-1 ring-[var(--btn)]'
                              : 'border-white/10 hover:border-white/25'
                          }`}
                        >
                          <div className="aspect-video bg-black/35">
                            {m.thumbnail_url ? (
                              <img src={m.thumbnail_url} alt={m.title || 'Clip'} className="w-full h-full object-cover" loading="lazy" />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center">
                                <Film className="w-5 h-5 text-[var(--rq-text-soft)]" />
                              </div>
                            )}
                            <div className={`absolute top-1 left-1 w-4 h-4 rounded border flex items-center justify-center ${
                              isSelected ? 'bg-[var(--btn)] border-[var(--btn)]' : 'border-white/40 bg-black/30'
                            }`}>
                              {isSelected && <Check className="w-3 h-3 text-white" />}
                            </div>
                          </div>
                          <div className="p-1">
                            <p className="text-[10px] text-[var(--rq-text-soft)] truncate">
                              {m.title || m.asset_filename || 'Untitled'}
                            </p>
                          </div>
                        </button>
                      )
                    })}
                  </div>
                )
              )}

              {addSourceTab === 'buckets' && (
                <div className="p-3 space-y-2">
                  {availableBuckets.length === 0 ? (
                    <div className="text-center py-8 text-sm text-[var(--rq-text-soft)]">No buckets available.</div>
                  ) : availableBuckets.map(item => (
                    <button
                      key={item.id}
                      onClick={() => addBucketToSection(item.id)}
                      className="w-full flex items-center justify-between px-4 py-3 bg-black/20 hover:bg-white/5 border border-white/10 hover:border-white/25 rounded-xl transition-all text-left"
                    >
                      <div>
                        <p className="text-sm text-[var(--rq-text)] font-medium">{item.name}</p>
                        <p className="text-xs text-[var(--rq-text-soft)]">{item.moments_count} clip{item.moments_count !== 1 ? 's' : ''}</p>
                      </div>
                      <Plus className="w-4 h-4 text-[var(--rq-text-soft)]" />
                    </button>
                  ))}
                </div>
              )}

              {addSourceTab === 'clusters' && (
                <div className="p-3 space-y-2">
                  {availableClusters.length === 0 ? (
                    <div className="text-center py-8 text-sm text-[var(--rq-text-soft)]">No clusters available.</div>
                  ) : availableClusters.map(item => (
                    <button
                      key={item.id}
                      onClick={() => addClusterToSection(item.id)}
                      className="w-full flex items-center justify-between px-4 py-3 bg-black/20 hover:bg-white/5 border border-white/10 hover:border-white/25 rounded-xl transition-all text-left"
                    >
                      <div>
                        <p className="text-sm text-[var(--rq-text)] font-medium">{item.name}</p>
                        <p className="text-xs text-[var(--rq-text-soft)]">{item.moments_count} clip{item.moments_count !== 1 ? 's' : ''}</p>
                      </div>
                      <Plus className="w-4 h-4 text-[var(--rq-text-soft)]" />
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="flex items-center justify-end gap-3">
              <button
                onClick={closeAddClips}
                className="rq-btn-ghost px-5 py-2.5 text-sm font-medium"
              >
                Cancel
              </button>
              {addSourceTab === 'clips' && (
                <button
                  onClick={submitAddClips}
                  disabled={addSelectedIds.length === 0 || addingClips}
                  className="rq-btn-primary px-5 py-2.5 disabled:opacity-40 text-sm"
                >
                  {addingClips ? 'Adding...' : `Add ${addSelectedIds.length > 0 ? addSelectedIds.length + ' ' : ''}Clip${addSelectedIds.length !== 1 ? 's' : ''}`}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
